import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { LeadAllocationFilters } from "./Filters";
import { LeadAllocationForm } from "./Form";
import { LeadAllocationTable } from "./Table";
import { LeadSummaryDetailDialog } from "./LeadSummaryDetailDialog";
import { LeadDetailDialog } from "./LeadDetailDialog";

interface ITeamMember {
  id: number;
  name: string;
  currentLeads: number;
  emails: number;
  calls: number;
  status?: string;
}

interface IMyLead {
  id: number;
  contactName: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  emailSent: boolean;
  callMade: boolean;
  createdAt: string;
}

export const LeadAllocation = ({
  hasPermission,
  toast,
}: {
  hasPermission: (pageName: string, featureName?: string) => boolean;
  toast?: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
  const [unassignedCount, setUnassignedCount] = useState<number>(0);
  const [showDialog, setShowDialog] = useState(false);
  const [allocationValues, setAllocationValues] = useState<{
    [key: number]: number;
  }>({});
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [description, setDescription] = useState<string>("");

  const localToast = useRef<any>(null);
  const [isTeamLead, setIsTeamLead] = useState<boolean | null>(null);

  // Date-wise summary state variables
  const [summaryList, setSummaryList] = useState<any[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showSummaryDetailDialog, setShowSummaryDetailDialog] = useState(false);
  const [summaryDetails, setSummaryDetails] = useState<any[]>([]);
  const [loadingSummaryDetails, setLoadingSummaryDetails] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [leadDetails, setLeadDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Filter state variables
  const [filterFromDate, setFilterFromDate] = useState<Date | undefined>(undefined);
  const [filterToDate, setFilterToDate] = useState<Date | undefined>(undefined);
  const [filterTeamLead, setFilterTeamLead] = useState<number | null>(null);
  const [filterTeamMember, setFilterTeamMember] = useState<number | null>(null);

  const [teamLeads, setTeamLeads] = useState<{ label: string; value: number }[]>([]);
  const [filteredTeamMembers, setFilteredTeamMembers] = useState<{ label: string; value: number }[]>([]);

  const handleFromDateChange = (date: Date | undefined) => {
    setFilterFromDate(date);
    if (date && filterToDate && date > filterToDate) {
      setFilterToDate(date);
    }
  };

  const handleToDateChange = (date: Date | undefined) => {
    if (date && filterFromDate && date < filterFromDate) {
      setFilterToDate(filterFromDate);
    } else {
      setFilterToDate(date);
    }
  };

  const handleClearFilters = () => {
    setFilterFromDate(undefined);
    setFilterToDate(undefined);
    setFilterTeamLead(null);
    setFilterTeamMember(null);
  };

  const getTeamMemberOptions = () => {
    const memberMap: { [key: number]: string } = {};

    // Add reporting team members
    teamMembers.forEach((member) => {
      memberMap[member.id] = member.name;
    });

    // Add unique members from summary list
    summaryList.forEach((item) => {
      if (item.teamMemberId) {
        memberMap[item.teamMemberId] = item.teamMemberName;
      }
    });

    return Object.keys(memberMap).map((idStr) => {
      const id = Number(idStr);
      return {
        label: memberMap[id],
        value: id,
      };
    });
  };

  const getTeamMemberDropdownOptions = () => {
    if (filterTeamLead) {
      return filteredTeamMembers;
    }
    return getTeamMemberOptions();
  };

  const getFilteredSummaryList = () => {
    return summaryList.filter((row) => {
      // Date filter
      if (row.allocationDateTime) {
        const rowDate = new Date(row.allocationDateTime);
        rowDate.setHours(0, 0, 0, 0);

        if (filterFromDate) {
          const from = new Date(filterFromDate);
          from.setHours(0, 0, 0, 0);
          if (rowDate < from) return false;
        }

        if (filterToDate) {
          const to = new Date(filterToDate);
          to.setHours(0, 0, 0, 0);
          if (rowDate > to) return false;
        }
      }

      // Team Lead filter
      if (filterTeamLead) {
        const isMemberOfLead = filteredTeamMembers.some((m) => m.value === row.teamMemberId) || row.teamMemberId === filterTeamLead;
        if (!isMemberOfLead) return false;
      }

      // Team Member filter
      if (filterTeamMember !== null && filterTeamMember !== undefined) {
        if (row.teamMemberId !== filterTeamMember) return false;
      }

      return true;
    });
  };

  const loadTeamLeads = async () => {
    try {
      const response = await Fetch("dropdown/getTeamLead", "GET", null, null, null);
      if (response) {
        const userList = await response.json();
        setTeamLeads(
          userList.map((ul: any) => ({
            label: ul.name,
            value: ul.id,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to load team leads", err);
    }
  };

  useEffect(() => {
    if (filterTeamLead) {
      const fetchTeamMembers = async () => {
        try {
          const res = await Fetch(
            "dropdown/user",
            "GET",
            null,
            `teamLeadId=${filterTeamLead}`,
            null
          );
          if (res) {
            const data = await res.json();
            setFilteredTeamMembers(
              data.map((m: any) => ({
                label: m.name,
                value: m.id,
              }))
            );
          }
        } catch (err) {
          console.error("Failed to load filtered team members", err);
        }
      };
      fetchTeamMembers();
      setFilterTeamMember(null);
    } else {
      setFilteredTeamMembers([]);
    }
  }, [filterTeamLead]);

  const loadData = async () => {
    setLoading(true);
    setLoadingSummary(true);
    try {
      if (hasPermission("Lead", "teamLeadFilter")) {
        loadTeamLeads();
      }

      // Try fetching team members to verify if the user is a Team Lead
      const membersRes = await Fetch(
        "LeadAllocation/team-members",
        "GET",
        null,
        null,
        { current: null }
      );
      if (membersRes) {
        const membersData = await membersRes.json();
        setTeamMembers(membersData);
        setIsTeamLead(true);

        const countRes = await Fetch(
          "LeadAllocation/unassigned-count",
          "GET",
          null,
          null,
          toast || localToast
        );
        if (countRes) {
          const countData = await countRes.json();
          setUnassignedCount(countData.count);
        }
      } else {
        setIsTeamLead(false);
      }

      // Fetch date-wise summary list
      const summaryRes = await Fetch(
        "LeadAllocation/summary",
        "GET",
        null,
        null,
        toast || localToast
      );
      if (summaryRes) {
        const summaryData = await summaryRes.json();
        setSummaryList(summaryData);
      }
    } catch (err) {
      console.error("Error loading lead allocation data", err);
    } finally {
      setLoading(false);
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openAllocation = (memberId?: number) => {
    // Initialize all inputs to 0
    const initialValues: { [key: number]: number } = {};
    teamMembers.forEach((member) => {
      initialValues[member.id] = (member.id === memberId && member.status === "Logged In") ? 10 : 0;
    });
    setAllocationValues(initialValues);
    setDescription("");
    setSelectedMemberId(memberId || null);
    setShowDialog(true);
  };

  const handleAllocateChange = (memberId: number, val: number | null) => {
    setAllocationValues((prev) => ({
      ...prev,
      [memberId]: val || 0,
    }));
  };

  const handleSaveAllocation = async () => {
    const payload = Object.keys(allocationValues)
      .map((idStr) => {
        const memberId = Number(idStr);
        const member = teamMembers.find((m) => m.id === memberId);
        return {
          teamMemberId: memberId,
          leadsToAllocate: member?.status === "Logged In" ? allocationValues[memberId] : 0,
          description: description || null,
        };
      })
      .filter((item) => item.leadsToAllocate > 0);

    if (payload.length === 0) {
      const activeToast = toast || localToast;
      activeToast.current?.show({
        severity: "warn",
        summary: "Validation Warning",
        detail: "Please enter at least one lead quantity to allocate.",
        life: 3000,
      });
      return;
    }

    const exceedsLimit = payload.find((item) => item.leadsToAllocate > 500);
    if (exceedsLimit) {
      const memberName = teamMembers.find((m) => m.id === exceedsLimit.teamMemberId)?.name || "User";
      const activeToast = toast || localToast;
      activeToast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: `Cannot allocate more than 500 leads to ${memberName}.`,
        life: 3000,
      });
      return;
    }

    const totalRequested = payload.reduce(
      (acc, curr) => acc + curr.leadsToAllocate,
      0
    );
    if (totalRequested > unassignedCount) {
      const activeToast = toast || localToast;
      activeToast.current?.show({
        severity: "error",
        summary: "Allocation Failed",
        detail: `Cannot allocate ${totalRequested} leads. Only ${unassignedCount} fresh leads are available.`,
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const res = await Fetch(
        "LeadAllocation/allocate",
        "POST",
        payload,
        null,
        toast || localToast
      );
      if (res) {
        const activeToast = toast || localToast;
        activeToast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Leads allocated successfully.",
          life: 3000,
        });
        setShowDialog(false);
        loadData();
      }
    } catch (err) {
      console.error("Allocation failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (rowData: any) => {
    setSelectedRowData(rowData);
    setShowSummaryDetailDialog(true);
    setLoadingSummaryDetails(true);
    setSummaryDetails([]);
    try {
      const res = await Fetch(
        "LeadAllocation/summary-details",
        "GET",
        null,
        `teamMemberId=${rowData.teamMemberId}&allocationId=${rowData.allocationId}`,
        toast || localToast
      );
      if (res) {
        const data = await res.json();
        setSummaryDetails(data);
      }
    } catch (err) {
      console.error("Failed to fetch summary details", err);
    } finally {
      setLoadingSummaryDetails(false);
    }
  };

  const handleToggleActivity = async (
    leadId: number,
    emailSent?: boolean,
    callMade?: boolean,
    emailDescription?: string,
    callDescription?: string
  ) => {
    // Optimistic update in summary details table view
    setSummaryDetails((prevDetails) =>
      prevDetails.map((lead) => {
        if (lead.id === leadId) {
          return {
            ...lead,
            emailSent: emailSent !== undefined ? (emailSent ? "Yes" : "No") : lead.emailSent,
            callMade: callMade !== undefined ? (callMade ? "Yes" : "No") : lead.callMade,
            emailDescription: emailDescription !== undefined ? emailDescription : lead.emailDescription,
            callDescription: callDescription !== undefined ? callDescription : lead.callDescription,
          };
        }
        return lead;
      })
    );

    // Optimistic update in details dialog if currently open and viewing the same lead
    setLeadDetails((prev: any) => {
      if (prev && prev.id === leadId) {
        return {
          ...prev,
          emailSent: emailSent !== undefined ? emailSent : prev.emailSent,
          callMade: callMade !== undefined ? callMade : prev.callMade,
          emailDescription: emailDescription !== undefined ? emailDescription : prev.emailDescription,
          callDescription: callDescription !== undefined ? callDescription : prev.callDescription,
        };
      }
      return prev;
    });

    try {
      const payload: {
        emailSent?: boolean;
        EmailSent?: boolean;
        callMade?: boolean;
        CallMade?: boolean;
        emailDescription?: string;
        EmailDescription?: string;
        callDescription?: string;
        CallDescription?: string;
      } = {};
      if (emailSent !== undefined) {
        payload.emailSent = emailSent;
        payload.EmailSent = emailSent;
        payload.emailDescription = emailDescription;
        payload.EmailDescription = emailDescription;
      }
      if (callMade !== undefined) {
        payload.callMade = callMade;
        payload.CallMade = callMade;
        payload.callDescription = callDescription;
        payload.CallDescription = callDescription;
      }

      const res = await Fetch(
        `LeadAllocation/my-leads/${leadId}/activity`,
        "PUT",
        payload,
        null,
        toast || localToast
      );
      if (!res) {
        // Rollback on failure
        loadData();
        if (selectedRowData) {
          handleRowClick(selectedRowData);
        }
      } else {
        const activeToast = toast || localToast;
        activeToast.current?.show({
          severity: "success",
          summary: "Updated",
          detail: "Activity status updated successfully.",
          life: 2000,
        });

        // Refresh summary list to update counters in summary table
        const summaryRes = await Fetch(
          "LeadAllocation/summary",
          "GET",
          null,
          null,
          { current: null }
        );
        if (summaryRes) {
          const summaryData = await summaryRes.json();
          setSummaryList(summaryData);
        }
      }
    } catch (err) {
      console.error("Failed to update activity", err);
    }
  };

  const handleViewLeadDetails = async (leadId: number) => {
    setShowDetailDialog(true);
    setLoadingDetails(true);
    setLeadDetails(null);
    try {
      const res = await Fetch(
        `LeadAllocation/my-leads/${leadId}`,
        "GET",
        null,
        null,
        toast || localToast
      );
      if (res) {
        const data = await res.json();
        setLeadDetails(data);
      }
    } catch (err) {
      console.error("Failed to fetch lead details", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleColumnClick = async (
    rowData: any,
    emailSent?: boolean,
    callMade?: boolean
  ) => {
    setSelectedRowData(rowData);
    let query = `teamMemberId=${rowData.teamMemberId}&allocationId=${rowData.allocationId}`;

    if (emailSent === true) {
      query += "&emailSent=true";
    }

    if (callMade === true) {
      query += "&callMade=true";
    }

    const res = await Fetch(
      "LeadAllocation/summary-details",
      "GET",
      null,
      query,
      toast || localToast
    );

    if (res) {
      const data = await res.json();
      setSummaryDetails(data);
      setShowSummaryDetailDialog(true);
    }
  };
  return (
    <div className="grid">
      <Toast ref={localToast} />
      {isTeamLead === null ? (
        <div className="col-12 flex justify-content-center align-items-center py-8">
          <i className="pi pi-spin pi-spinner text-primary text-4xl"></i>
        </div>
      ) : isTeamLead ? (
        // ==================== TEAM LEAD VIEW ====================
        <>
          <div className="col-12">
            <div className="card mb-4 flex justify-content-between align-items-center bg-white p-4 border-round shadow-1" style={{ border: '1px solid #e2e8f0' }}>
              <div>
                <h5
                  className="m-0 text-bluegray-800 font-bold"
                  style={{ fontSize: "1.25rem" }}
                >
                  Lead Allocation Dashboard
                </h5>
              </div>
              <div className="flex align-items-center gap-4">
                <div className="text-right mr-3">
                  <span
                    className="block text-bluegray-500 font-semibold mb-1"
                    style={{ fontSize: "0.8rem", textTransform: "uppercase", whiteSpace: "nowrap" }}
                  >
                    Available Unassigned Leads
                  </span>
                  <span className="text-3xl font-bold text-primary">
                    {unassignedCount.toLocaleString()}
                  </span>
                </div>
                {/* {(hasPermission("LeadAllocation", "Can Allocate") && <Button
                  label="Allocate Leads"
                  icon="pi pi-plus"
                  className="p-button-primary p-button-rounded"
                  onClick={() => openAllocation()}
                />) || null} */}
                <Button
                  label="Allocate Leads"
                  icon="pi pi-plus"
                  className="p-button-primary p-button-rounded"
                  onClick={() => openAllocation()}
                />
              </div>
            </div>
          </div>

          <LeadAllocationFilters
            filterFromDate={filterFromDate}
            filterToDate={filterToDate}
            filterTeamLead={filterTeamLead}
            filterTeamMember={filterTeamMember}
            showTeamLeadFilter={hasPermission("Lead", "teamLeadFilter")}
            showTeamMemberFilter={hasPermission("LeadAllocation", "showTeamMemberFilter")}

            teamLeadOptions={teamLeads}
            teamMemberOptions={getTeamMemberDropdownOptions()}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
            onTeamLeadChange={setFilterTeamLead}
            onTeamMemberChange={setFilterTeamMember}
            onClear={handleClearFilters}
          />

          <div className="col-12 mt-3">
            <LeadAllocationTable
              value={getFilteredSummaryList()}
              loading={loadingSummary}
              onColumnClick={handleColumnClick}
            />
          </div>

          <LeadAllocationForm
            visible={showDialog}
            onHide={() => setShowDialog(false)}
            unassignedCount={unassignedCount}
            teamMembers={teamMembers}
            allocationValues={allocationValues}
            selectedMemberId={selectedMemberId}
            onAllocateChange={handleAllocateChange}
            description={description}
            onDescriptionChange={setDescription}
            onSave={handleSaveAllocation}
            loading={loading}
          />
        </>
      ) : (
        // ==================== TEAM MEMBER VIEW ====================
        <>
          <div className="col-12">
            <div className="card mb-4 flex justify-content-between align-items-center bg-white p-4 border-round shadow-1" style={{ border: '1px solid #e2e8f0' }}>
              <div>
                <h5
                  className="m-0 text-bluegray-800 font-bold"
                  style={{ fontSize: "1.25rem" }}
                >
                  My Allocated Leads
                </h5>
              </div>
            </div>
          </div>

          <LeadAllocationFilters
            filterFromDate={filterFromDate}
            filterToDate={filterToDate}
            filterTeamLead={filterTeamLead}
            filterTeamMember={filterTeamMember}
            showTeamLeadFilter={hasPermission("Lead", "teamLeadFilter")}
            showTeamMemberFilter={hasPermission("LeadAllocation", "showTeamMemberFilter")}
            teamLeadOptions={teamLeads}
            teamMemberOptions={getTeamMemberDropdownOptions()}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
            onTeamLeadChange={setFilterTeamLead}
            onTeamMemberChange={setFilterTeamMember}
            onClear={handleClearFilters}
          />

          <div className="col-12 mt-3">
            <LeadAllocationTable
              value={getFilteredSummaryList()}
              loading={loadingSummary}
              onColumnClick={handleColumnClick}
            />
          </div>
        </>
      )}

      <LeadSummaryDetailDialog
        visible={showSummaryDetailDialog}
        onHide={() => setShowSummaryDetailDialog(false)}
        selectedRowData={selectedRowData}
        loadingSummaryDetails={loadingSummaryDetails}
        summaryDetails={summaryDetails}
        onToggleActivity={handleToggleActivity}
        onViewLeadDetails={handleViewLeadDetails}
      />

      <LeadDetailDialog
        visible={showDetailDialog}
        onHide={() => setShowDetailDialog(false)}
        loadingDetails={loadingDetails}
        leadDetails={leadDetails}
        onToggleActivity={handleToggleActivity}
        setLeadDetails={setLeadDetails}
      />
    </div>
  );
};
